"use client";

import * as React from "react";
import { MoreHorizontalIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Spinner } from "./spinner";
import { Tooltip } from "./tooltip";
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuGroup,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubTrigger,
	DropdownMenuSubContent,
} from "./dropdown-menu";

// ---------------------------------------------------------------------------
// Context -- shares open-state setter so Items can hold the menu open during
// async work.
// ---------------------------------------------------------------------------

type ActionsMenuContextValue = {
	setOpen: (open: boolean) => void;
};

const ActionsMenuContext = React.createContext<ActionsMenuContextValue | null>(
	null,
);

function useActionsMenuContext() {
	const ctx = React.useContext(ActionsMenuContext);
	if (!ctx) {
		throw new Error(
			"ActionsMenu compound components must be used within <ActionsMenu>",
		);
	}
	return ctx;
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

type ActionsMenuRootProps = {
	children: React.ReactNode;
	/** Controlled open state. */
	open?: boolean;
	/** Callback when the open state changes. */
	onOpenChange?: (open: boolean) => void;
	/** Whether the menu is modal (traps focus). Defaults to `false`. */
	modal?: boolean;
};

function ActionsMenuRoot({
	children,
	open: controlledOpen,
	onOpenChange,
	modal,
}: ActionsMenuRootProps) {
	const [internalOpen, setInternalOpen] = React.useState(false);
	const isControlled = controlledOpen !== undefined;
	const open = isControlled ? controlledOpen : internalOpen;

	const setOpen = React.useCallback(
		(next: boolean) => {
			if (!isControlled) {
				setInternalOpen(next);
			}
			onOpenChange?.(next);
		},
		[isControlled, onOpenChange],
	);

	const ctx = React.useMemo(() => ({ setOpen }), [setOpen]);

	return (
		<ActionsMenuContext.Provider value={ctx}>
			<DropdownMenu open={open} onOpenChange={setOpen} modal={modal}>
				{children}
			</DropdownMenu>
		</ActionsMenuContext.Provider>
	);
}

// ---------------------------------------------------------------------------
// Trigger -- defaults to a three-dot icon button
// ---------------------------------------------------------------------------

type ActionsMenuTriggerProps = Omit<
	React.ComponentProps<typeof DropdownMenuTrigger>,
	"children"
> & {
	children?: React.ReactNode;
	/** Test identifier. Applied to the default trigger button. */
	"data-testid"?: string;
};

function ActionsMenuTrigger({
	children,
	asChild,
	className,
	"aria-label": ariaLabel,
	"data-testid": dataTestId,
	...props
}: ActionsMenuTriggerProps) {
	if (children) {
		return (
			<DropdownMenuTrigger
				asChild={asChild}
				className={className}
				aria-label={ariaLabel}
				data-testid={dataTestId}
				{...props}
			>
				{children}
			</DropdownMenuTrigger>
		);
	}

	// `aria-label` and `data-testid` go on the Button rather than the trigger: Radix `Slot` lets the
	// child's props win, so anything set here would otherwise lose to the Button's own values.
	// The default name is only for this icon button — custom children keep their own accessible name.
	return (
		<DropdownMenuTrigger asChild {...props}>
			<Button
				type="button"
				variant="outline"
				size="icon-sm"
				aria-label={ariaLabel ?? "More actions"}
				data-testid={dataTestId}
				className={className}
			>
				<MoreHorizontalIcon className="size-4" />
			</Button>
		</DropdownMenuTrigger>
	);
}

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

type ActionsMenuContentProps = React.ComponentProps<typeof DropdownMenuContent>;

function ActionsMenuContent({
	align = "end",
	...props
}: ActionsMenuContentProps) {
	return <DropdownMenuContent align={align} side="bottom" {...props} />;
}

// ---------------------------------------------------------------------------
// Item -- enhanced with async onSelect + disabled tooltip
// ---------------------------------------------------------------------------

type ActionsMenuItemProps = Omit<
	React.ComponentProps<typeof DropdownMenuItem>,
	"onSelect"
> & {
	/** Icon rendered before the label. */
	icon?: React.ReactNode;
	/** Called when the item is selected. If a Promise is returned the menu stays
	 *  open and a spinner is shown until it resolves. */
	onSelect?: () => void | Promise<void>;
	/** Tooltip shown when the item is disabled. */
	disabledReason?: string;
	/** Test identifier. */
	"data-testid"?: string;
};

function ActionsMenuItemInner({
	children,
	icon,
	onSelect,
	disabled,
	disabledReason,
	className,
	"data-testid": dataTestId,
	...props
}: ActionsMenuItemProps) {
	const { setOpen } = useActionsMenuContext();
	const [loading, setLoading] = React.useState(false);

	const handleSelect = React.useCallback(
		async (e: Event) => {
			e.preventDefault();
			if (!onSelect) {
				return;
			}

			const result = onSelect();

			if (result instanceof Promise) {
				setLoading(true);
				try {
					await result;
					setOpen(false);
				} catch {
					// Keep the menu open on failure so the user can retry.
				} finally {
					setLoading(false);
				}
			} else {
				setOpen(false);
			}
		},
		[onSelect, setOpen],
	);

	const content = (
		<DropdownMenuItem
			onSelect={handleSelect}
			disabled={disabled || loading}
			className={cn(
				disabled && disabledReason ? "cursor-not-allowed" : "cursor-pointer",
				className,
			)}
			data-testid={dataTestId}
			{...props}
		>
			{icon && <span className="mr-2">{icon}</span>}
			{children}
			{loading && <Spinner className="ml-auto size-4" />}
		</DropdownMenuItem>
	);

	if (disabled && disabledReason) {
		return (
			<Tooltip content={disabledReason} asChild>
				<div tabIndex={-1}>{content}</div>
			</Tooltip>
		);
	}

	return content;
}

// ---------------------------------------------------------------------------
// Group -- thin wrapper with optional label
// ---------------------------------------------------------------------------

type ActionsMenuGroupProps = React.ComponentProps<typeof DropdownMenuGroup> & {
	label?: string;
};

function ActionsMenuGroup({
	label,
	children,
	...props
}: ActionsMenuGroupProps) {
	return (
		<DropdownMenuGroup {...props}>
			{label && (
				<DropdownMenuLabel className="px-2 py-1 text-sm text-muted-foreground">
					{label}
				</DropdownMenuLabel>
			)}
			{children}
		</DropdownMenuGroup>
	);
}

// ---------------------------------------------------------------------------
// Separator
// ---------------------------------------------------------------------------

function ActionsMenuSeparator(
	props: React.ComponentProps<typeof DropdownMenuSeparator>,
) {
	return <DropdownMenuSeparator {...props} />;
}

// ---------------------------------------------------------------------------
// Label
// ---------------------------------------------------------------------------

function ActionsMenuLabel(
	props: React.ComponentProps<typeof DropdownMenuLabel>,
) {
	return <DropdownMenuLabel {...props} />;
}

// ---------------------------------------------------------------------------
// Sub / SubTrigger / SubContent
// ---------------------------------------------------------------------------

function ActionsMenuSub(props: React.ComponentProps<typeof DropdownMenuSub>) {
	return <DropdownMenuSub {...props} />;
}

function ActionsMenuSubTrigger(
	props: React.ComponentProps<typeof DropdownMenuSubTrigger>,
) {
	return <DropdownMenuSubTrigger {...props} />;
}

function ActionsMenuSubContent(
	props: React.ComponentProps<typeof DropdownMenuSubContent>,
) {
	return <DropdownMenuSubContent {...props} />;
}

// ---------------------------------------------------------------------------
// Compound export
// ---------------------------------------------------------------------------

export const ActionsMenu = Object.assign(ActionsMenuRoot, {
	Trigger: ActionsMenuTrigger,
	Content: ActionsMenuContent,
	Item: ActionsMenuItemInner,
	Group: ActionsMenuGroup,
	Separator: ActionsMenuSeparator,
	Label: ActionsMenuLabel,
	Sub: ActionsMenuSub,
	SubTrigger: ActionsMenuSubTrigger,
	SubContent: ActionsMenuSubContent,
});

export type {
	ActionsMenuRootProps,
	ActionsMenuTriggerProps,
	ActionsMenuContentProps,
	ActionsMenuItemProps,
	ActionsMenuGroupProps,
};
